import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthApi from '../../../../service/Api/authApi';
import { gaEvents } from '../../../../utils/analytics';
import CloseIcon from '../../../icons/CloseIcon';
import ArrowLeftIcon from '../../../icons/subscriptionIcons/ArrowLeftIcon';
import BigThree from './BigThree';
import ChartCtas from './ChartCtas';
import ChartTab from './ChartTab';
import Tabs from './Tabs';
import { capitalize, formatBirthDate } from './chartGeometry';
import styles from './styles.module.scss';

/**
 * The birth chart screen (HORO-10360, Figma 1566:12981). A pure render of the
 * server's BirthChartReportDTO.
 *
 *   variant 'modal' — sheet over the chat with a close X (`onClose`); the two
 *                    "what you can do" CTAs close via `onCtaClose` when given
 *   variant 'page'  — fills its container with a back arrow (`onBack`)
 *
 * `surface` (BIRTH_CHART_VIEW_SURFACE) is where the view started; it is reported
 * to the server so Weekly Reporting can count viewers. No surface, no report.
 */
const BirthChartView = ({ report, variant = 'modal', surface, onClose, onCtaClose, onBack }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('bigThree');
    const viewRef = useRef(null);
    const bodyRef = useRef(null);
    const isModal = variant === 'modal';
    const { subject, content } = report;
    const { bigThree } = content;

    useEffect(() => {
        gaEvents.birthChartResultsViewed(bigThree.sun?.sign || '', bigThree.moon?.sign || '', bigThree.rising?.sign || '');
        // Once per mount: the report does not change under an open view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The server counts viewers from this, once per open. Best-effort: a failed
    // report must never get in the way of reading the chart.
    useEffect(() => {
        if (!surface) return;
        Promise.resolve(AuthApi.trackBirthChartView(surface)).catch(() => {});
        // Once per mount, like the GA event above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isModal || !onClose) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isModal, onClose]);

    // Both tabs share one scroll position. Scrolled down the Birth Chart tab and
    // back to Big Three, the new tab opened part-way down with its heading and
    // intro scrolled out of sight under the header and tabs. Start every tab at
    // its own top.
    const changeTab = (next) => {
        setTab(next);
        requestAnimationFrame(() => {
            if (isModal) {
                bodyRef.current?.scrollTo?.({ top: 0 });
            } else if (viewRef.current && viewRef.current.getBoundingClientRect().top < 0) {
                // The page scrolls (the document, or the profile's detail pane): bring the
                // top of the view back, clear of the fixed site header (scroll-margin).
                viewRef.current.scrollIntoView({ block: 'start' });
            }
        });
    };

    const sunSign = capitalize(subject.sunSign || '');
    const subline = [formatBirthDate(subject.birthDate), sunSign].filter(Boolean);

    const body = (
        <div ref={viewRef} className={classNames(styles.view, { [styles.asPage]: !isModal })} data-variant={variant}>
            <header className={styles.header}>
                {isModal ? (
                    <>
                        <div className={styles.who}>
                            <h2 className={styles.name}>{subject.name || t('birthChart.view.yourChart')}</h2>
                            {subline.length > 0 && <p className={styles.sub}>{subline.join(' · ')}</p>}
                        </div>
                        <button type="button" className={styles.close} onClick={onClose} aria-label={t('birthChart.view.close')}>
                            <CloseIcon stroke="#FFFFFF" />
                        </button>
                    </>
                ) : (
                    <>
                        <button type="button" className={styles.back} onClick={onBack} aria-label={t('birthChart.view.back')}>
                            <ArrowLeftIcon />
                        </button>
                        <h2 className={styles.pageTitle}>{t('birthChart.view.pageTitle')}</h2>
                    </>
                )}
            </header>

            {!isModal && (subject.name || subline.length > 0) && (
                <div className={classNames(styles.who, styles.whoPage)}>
                    {subject.name && <p className={styles.name}>{subject.name}</p>}
                    {subline.length > 0 && <p className={styles.sub}>{subline.join(' · ')}</p>}
                </div>
            )}

            <Tabs
                label={t('birthChart.view.tabs.label')}
                active={tab}
                onChange={changeTab}
                tabs={[
                    { id: 'bigThree', label: t('birthChart.view.tabs.bigThree') },
                    { id: 'chart', label: t('birthChart.view.tabs.chart') }
                ]}
            />

            <div ref={bodyRef} className={styles.body}>
                {tab === 'bigThree' ? (
                    <BigThree bigThree={bigThree} onSelect={(key, sign) => gaEvents.birthChartPlacementViewed(key, sign)}>
                        <ChartCtas variant={variant} onClose={onClose} onCtaClose={onCtaClose} />
                    </BigThree>
                ) : (
                    <ChartTab report={report}>
                        <ChartCtas variant={variant} onClose={onClose} onCtaClose={onCtaClose} />
                    </ChartTab>
                )}
            </div>
        </div>
    );

    if (!isModal) return body;

    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={subject.name || t('birthChart.view.yourChart')}>
                {body}
            </div>
        </div>
    );
};

export default BirthChartView;
